import React, { useCallback, useState } from "react";
import { Container } from "reactstrap";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import { CSV_TEMPLATES } from "utils/constants";
import uploadIcon from "assets/images/svg/Upload.svg";
import NGIDropdownButton from "components/Common/buttons/NGIDropdownButton";
import * as xlsx from "xlsx";
import { defaultNgiErrorMethod, errorMessage, successMessage } from "components/Common/responses/message";
import { getCustomerConvertUploadUrl, uploadDataToS3BucketByUrl } from "utils/api";
import { Modal, Space, Spin } from "antd";
import CustomLink from "components/Common/buttons/CustomLink";
import { generateCSV } from "utils/helpers/functions";
import closeIcon from "assets/images/svg/closeSmall.svg";
import Dragger from "antd/lib/upload/Dragger";
import uploadArea from "assets/images/svg/uploadArea.svg";

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  downloadTitleContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  fileContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 15,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    padding: "5px 10px",
    marginTop: 10,
  },
  removeBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 32,
    width: 32,
    borderRadius: 50,
    background: "rgba(255, 255, 255, 1)",

    "&:hover": {
      cursor: "pointer",
      opacity: 0.6,
    },
  },
});

const UPLOAD_TYPE = {
  RESIDENTIAL: "Residential",
  COMMERCIAL: "Commercial",
};
const CustomerConvert = () => {
  const classes = useStyles();
  const [type, setType] = useState(null);

  const openUploadModal = useCallback(type => setType(type), []);
  const closeUploadModal = useCallback(() => setType(null), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className={classes.root}>
            <div className={classes.buttonsContainer}>
              <NGIDropdownButton
                toggleEl={<CustomButton iconImg={uploadIcon} type="primary" text="Convert Customers" />}
                menuItems={[
                  {
                    label: "Convert to Commercial",
                    callback: () => openUploadModal(UPLOAD_TYPE.COMMERCIAL),
                  },
                  {
                    label: "Convert to Residential",
                    callback: () => openUploadModal(UPLOAD_TYPE.RESIDENTIAL),
                  },
                ]}
              />
            </div>
          </div>
        </Container>
        {type && <ConvertModal type={type} onClose={closeUploadModal} />}
      </div>
    </>
  );
};

export default CustomerConvert;

const ConvertModal = ({ type, onClose }) => {
  const classes = useStyles();
  const [dataLoading, setDataLoading] = useState(false);
  const [file, setFile] = useState(null);

  const readUploadFile = useCallback(e => {
    const file = e.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        const data = e.target.result;
        const workbook = xlsx.read(data);
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet, { raw: false });
        const formattedResult = json.map(item => {
          if (item.customerId) return item.customerId ? item.customerId : null;
          return null;
        });
        const noError = formattedResult.every(item => item || null);
        !noError && errorMessage("Please upload the correct ‘*.csv’ or ‘*.xlsx’ file with customerId fields");

        if (noError) {
          setFile(file);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const onSubmit = useCallback(async () => {
    setDataLoading(true);
    getCustomerConvertUploadUrl(type)
      .then(res => {
        const {
          result: { url },
        } = res?.data;
        uploadDataToS3BucketByUrl(url, file).then(() => {
          setDataLoading(false);
          onClose();
          successMessage(`Customers converted to ${type} successfully`);
        });
      })
      .catch(err => {
        setDataLoading(false);
        defaultNgiErrorMethod(err);
      });
  }, [file, type]);

  const removeFile = useCallback(() => {
    setFile(null);
  }, []);

  return (
    <Modal title={`Convert to ${type}`} open onCancel={onClose} footer={[]} closable={false} onOk={onSubmit}>
      <div data-testid={"meters-upload-modal"}>
        <Spin spinning={dataLoading}>
          <div className={classes.downloadTitleContainer}>
            <CustomLink color="#0085FF" onClick={() => generateCSV(CSV_TEMPLATES.CUSTOMER_CONVERT)}>
              Download *.csv template
            </CustomLink>
          </div>
          {file ? (
            <div className={classes.fileContainer}>
              <div className={classes.fileTitle}>{`${file.name}`}</div>
              <div onClick={removeFile} className={classes.removeBtn}>
                <img src={closeIcon} alt="remove" />
              </div>
            </div>
          ) : (
            <Dragger showUploadList={false} customRequest={readUploadFile} name="file">
              <img className="upload-bg" src={uploadArea} alt="upload" />
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for a single upload. Strictly prohibit from uploading company data or other band files
              </p>
            </Dragger>
          )}
          <br />

          <Space>
            <CustomButton onClick={onClose} type="primary" size="small" color="outlined" text="Cancel" />

            <CustomButton
              onClick={onSubmit}
              disabled={!file}
              htmlType="submit"
              type="primary"
              text="Upload"
              size="small"
            />
          </Space>
        </Spin>
      </div>
    </Modal>
  );
};
